import React, { useState } from 'react';
import logger from '../../utils/logger';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Role } from '../../types';
import { ShieldCheckIcon, UserGroupIcon, UserIcon, EyeIcon } from '@heroicons/react/24/solid';

const DemoSelectionPage: React.FC = () => {
  const { loginDemo } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string>('');

  // スタイル定数
  const roleButtonClassName = [
    'group flex flex-col text-left p-5 rounded-lg',
    'bg-white dark:bg-gray-700',
    'hover:bg-blue-100 dark:hover:bg-blue-900/40',
    'border-2 border-gray-200 dark:border-gray-600',
    'hover:border-blue-500 dark:hover:border-blue-400',
    'hover:outline hover:outline-2 hover:outline-blue-500 dark:hover:outline-blue-400',
    'transition-all duration-300 hover:scale-105',
    'hover:shadow-xl hover:shadow-blue-500/20 dark:hover:shadow-blue-500/30',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'disabled:hover:bg-gray-50 dark:disabled:hover:bg-gray-700',
    'disabled:hover:scale-100 disabled:hover:shadow-sm',
    'h-full shadow-sm transform',
  ].join(' ');

  // デモログインハンドラー
  const handleDemoLogin = async (role: Role) => {
    setIsLoading(true);
    setLoginError('');

    try {
      await loginDemo(role);
      navigate('/dashboard');
    } catch (error) {
      logger.error('Demo login error:', error);
      setLoginError('デモログインに失敗しました。再度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  const demoRoles = [
    {
      role: Role.ADMIN,
      title: '陸送会社管理者',
      icon: <ShieldCheckIcon className='h-6 w-6 mr-3 text-blue-600 dark:text-blue-400' />,
      description: 'マスターデータを含む、システムへのフルアクセス。',
      badge: '全機能',
      color: 'bg-blue-500',
    },
    {
      role: Role.DISPATCHER,
      title: '配車・営業担当者',
      icon: <UserGroupIcon className='h-6 w-6 mr-3 text-green-600 dark:text-green-400' />,
      description: '案件管理とAI自動配車を実行します。',
      badge: '業務管理',
      color: 'bg-green-500',
    },
    {
      role: Role.DRIVER,
      title: '陸送員',
      icon: <UserIcon className='h-6 w-6 mr-3 text-orange-600 dark:text-orange-400' />,
      description: '割り当てられた案件の表示とステータス報告。',
      badge: '現場作業',
      color: 'bg-orange-500',
    },
    {
      role: Role.READ_ONLY,
      title: '閲覧専用',
      icon: <EyeIcon className='h-6 w-6 mr-3 text-gray-600 dark:text-gray-400' />,
      description: 'レポートとデータへの閲覧専用アクセス。',
      badge: '読取専用',
      color: 'bg-gray-500',
    },
  ];

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center items-center p-4'>
      <div className='max-w-2xl w-full mx-auto'>
        {/* ヘッダー */}
        <div className='text-center mb-8'>
          <div className='flex justify-center items-center mb-6'>
            <img src='/logo.webp' alt='ラクハイ' className='h-12 w-12 sm:h-16 sm:w-16' />
            <h2 className='ml-3 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white'>
              ラクハイ - デモ体験
            </h2>
          </div>
          <p className='text-gray-600 dark:text-gray-300 mb-4'>
            役割を選択してデモ環境を体験してください
          </p>
        </div>

        {/* デモ環境情報 */}
        <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6'>
          <div className='flex items-start'>
            <div className='flex-shrink-0'>
              <div className='w-3 h-3 bg-blue-400 rounded-full animate-pulse mt-1'></div>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                デモ環境について
              </h3>
              <div className='text-xs text-blue-700 dark:text-blue-300 mt-1'>
                <p>• サンプル会社「株式会社サンプル陸送」のデータを使用</p>
                <p>• すべての機能を安全にお試しいただけます</p>
                <p>• データの変更や削除は他のユーザーに影響しません</p>
              </div>
            </div>
          </div>
        </div>

        {/* エラー表示 */}
        {loginError && (
          <div className='mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <p className='text-sm text-red-800 dark:text-red-200'>{loginError}</p>
          </div>
        )}

        {/* ロール選択 */}
        <div className='bg-white dark:bg-gray-800 shadow-lg rounded-xl p-6'>
          <h2 className='text-xl font-semibold text-center text-gray-700 dark:text-gray-200 mb-6'>
            体験したい役割を選択
          </h2>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {demoRoles.map(({ role, title, icon, description, badge, color }) => (
              <button
                key={role}
                onClick={() => handleDemoLogin(role)}
                disabled={isLoading}
                className={roleButtonClassName}
              >
                <div className='flex items-start justify-between mb-3'>
                  <div className='flex items-center'>
                    {isLoading ? (
                      <div className='h-6 w-6 mr-3 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600'></div>
                    ) : (
                      icon
                    )}
                    <span className='font-semibold text-gray-800 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-300'>
                      {title}
                    </span>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium text-white rounded-full ${color}`}
                  >
                    {badge}
                  </span>
                </div>

                <p className='text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300 flex-1'>
                  {isLoading ? 'ログイン中...' : description}
                </p>

                <div className='mt-3 pt-3 border-t border-gray-200 dark:border-gray-600'>
                  <span className='text-xs text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 font-medium transition-colors duration-300'>
                    {isLoading ? '処理中...' : 'クリックして体験開始 →'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* フッター */}
        <p className='text-center text-sm text-gray-500 mt-8'>
          © 2025 ラクハイ All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default DemoSelectionPage;
